import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import CTA from 'Src/components/buttons/CTA';
import { CardReadSuccessOptions, PaxDataDef } from 'Src/feature/card-reader/useCardReader';
import { useCashDrawer } from 'Src/feature/cash-drawer/useCashDrawer';
import { Check } from 'Src/feature/check/models';
import { useCheck } from 'Src/feature/check/useCheck';
import { useCurrentCheck } from 'Src/feature/check/useCurrentCheck';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { useDOMEventListener } from 'Src/feature/dom-event/useDOMEventListener';
import { DebitGiftCardRequest } from 'Src/feature/gift-card/giftCardService';
import { useGiftCard } from 'Src/feature/gift-card/useGiftCard';
import { useModalControl } from 'Src/feature/modal/useModalControl';
import { useCurrentOperator } from 'Src/feature/operator/useCurrentOperator';
import { useCardReaderControl } from 'Src/feature/payment/useCardReaderControl';
import { useCheckPaymentModal } from 'Src/feature/payment/useCheckPaymentModal';
import { usePaymentFlow } from 'Src/feature/payment/usePaymentFlow';
import { ManualPaymentMessage, useManualPaymentWindow } from 'Src/feature/payment-manual/useManualPaymentWindow';
import { usePrinter } from 'Src/feature/printer/usePrinter';
import { useRevenueCenters } from 'Src/feature/revenue-center/useRevenueCenter';
import { useTerminalSettings } from 'Src/feature/terminal/useTerminalSettings';
import { useToast } from 'Src/feature/toasts/useToast';
import { Uuid } from 'Src/models/Uuid';
import { ServiceError, useError } from 'Src/network/useError';
import useRoute from 'Src/routes/useRoute';
import { uniqueInt } from 'Src/utils/number';
import { sortByLabel } from 'Src/utils/sort';

import { duplicateCardVerify } from './duplicateCardVerify';
import { CardReaderRequest, CardReaderType, EPXIndustry, ManualCCTransactionRequest } from './paymentService';
import { useCheckPagePaymentPermissions } from './useCheckPagePaymentPermissions';
import { useCurrentPayment } from './useCurrentPayment';
import { usePayment } from './usePayment';
import { usePaymentMethod } from './usePaymentMethod';

const sectionId = 'useCheckPagePayment';

type ExecuteModalActionOption =
  | { type: 'cash' }
  | { type: 'manual'; amount: number; }
  | { type: 'gc'; }
  | { type: 'other'; }
  | { type: 'preAuth'; payment: Check['payments'][number]; amount: number; }

type PaymentCompleteOptions =
  | { intent: 'error', error: unknown; }
  | { intent: 'modal' }
  ;

const useCheckPagePayment = () => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const { t } = useTranslation();
  const { navigate, route, crumbs } = useRoute();
  const toast = useToast({ persist: true });
  const { parseError } = useError();

  const currentOperator = useCurrentOperator();
  const currentCheck = useCurrentCheck();
  const currentPayment = useCurrentPayment();
  const cashDrawer = useCashDrawer();
  const paymentFlow = usePaymentFlow();
  const grantedPermissions = useCheckPagePaymentPermissions();

  const checkInvalidate = useCheck.query.invalidate();
  const ccPaymentMethods = usePaymentMethod.query.ccMethods();
  const cashPaymentMethods = usePaymentMethod.query.cashMethods();
  const gcPaymentMethods = usePaymentMethod.query.giftCardMethods();
  const paymentMethodMap = usePaymentMethod.query.map();
  const revenueCenters = useRevenueCenters.query.getAll();
  const balanceSplits = useCheck.mutate.balanceSplits();
  const printerSend = usePrinter.mutate.send();
  const updateStatus = useCheck.mutate.updateStatus();
  const giftCardDebit = useGiftCard.mutate.debit();
  const giftCardBalance = useGiftCard.mutate.balance();
  const giftCardVoidDebit = useGiftCard.mutate.void();
  const epxPreAuthManual = usePayment.mutate.epxPreAuthManual();
  const epxSaleManual = usePayment.mutate.epxSaleManual();
  const adjust = usePayment.mutate.adjust();
  const createCash = usePayment.mutate.createCash();
  const other = usePayment.mutate.other();
  const removePayment = usePayment.mutate.removePayment();
  const preAuth = usePayment.mutate.preAuth();
  const sale = usePayment.mutate.sale();
  const paymentResponseDeclined = usePayment.response.declined();
  const { data: terminalSettings } = useTerminalSettings.query();

  const check = currentCheck.query.data;
  // "checkId" - used as the instigator for when this hook first mounts
  // - used in a useEffect to initialize unpaid amount
  // - used in a useEffect to initialize split amount when coming back from flip and tipe
  const checkId = check?.id;

  // TODO: remove this ref and isolate the manualCC and duplicate card logic into their own hooks
  const ref = useRef({
    manualCCAmount: undefined as undefined | number,
    duplicateCardDetails: undefined as undefined | { cardBrand: string, lastFour: string, prevAmount: number, nextAmount: number },
  });

  const flipAndTip = {
    navigate: ({ checkId, paymentId }: { checkId: Check['id']; paymentId: Check['payments'][number]['id'] }) => {
      // !!! must reset the check again so we have updated payment information when we get to the flip and tip page
      checkInvalidate.getOne(checkId);
      navigate(crumbs.pos.customer.flipAndTip.start.interpolate({ checkId, paymentId }));
    }
  };

  const ccPaymentMethodOptions = (ccPaymentMethods.data ?? [])
    .filter((pm) => grantedPermissions.ccPayment.paymentMethodIds.includes(pm.id))
    .map((pm) => ({ value: pm.id as number, label: pm.name }))
    .sort(sortByLabel);

  const gcPaymentMethodOptions = (gcPaymentMethods.data ?? [])
    .filter((pm) => grantedPermissions.giftCardPayment.paymentMethodIds.includes(pm.id))
    .map((pm) => ({ value: pm.id as number, label: pm.name }))
    .sort(sortByLabel);

  const manualCCMethodOptions = useMemo(() => (ccPaymentMethods.data ?? [])
    .filter((pm) => grantedPermissions.manualPayment.paymentMethodIds.includes(pm.id))
    .filter((ccm) => ccm.fields?.allowManualCardNotPresent === true)
    .map((pm) => ({ value: pm.id as number, label: pm.name }))
    .sort(sortByLabel) ?? [], [ccPaymentMethods.data, grantedPermissions.manualPayment.paymentMethodIds]);

  const giftCardPayment = async (options?: { paymentMethodId?: number; }) => {
    if (typeof checkId !== 'number') return log.debug('giftCardPayment', '!checkId');

    if (gcPaymentMethodOptions.length < 1) {
      currentPayment.setStatus('No gift card payment methods set up to work with card reader...');
      cardReader.reinit('giftCardPayment !gcPaymentMethodOptions');
      return;
    }

    const payMethodWithMatchingMerchId = (gcPaymentMethods.data ?? [])
      .find(({ docsOnlyFieldsGiftCard }) => docsOnlyFieldsGiftCard?.merchantId === cardReader.read?.pax.giftcardData.merchId);

    if (gcPaymentMethodOptions.length > 1 && !options?.paymentMethodId && !payMethodWithMatchingMerchId) return modal.giftCardMethodPayment.open();

    modal.giftCardMethodPayment.close('on-option-selected');

    const account = cardReader.read?.pax.giftcardData.number;

    if (typeof account !== 'string') return log.debug('giftCardPayment', 'account number should be defined');

    paymentFlow.do('gc-account-number', account);

    const paymentMethodId = payMethodWithMatchingMerchId?.id ?? options?.paymentMethodId ?? gcPaymentMethodOptions[0].value;

    let debitGiftCardRequest = undefined as undefined | DebitGiftCardRequest;

    // check gift card balance and throw an error if no funds
    try {
      const gcBalResp = await giftCardBalance.mutateAsync({ giftCardBalanceRequest: { account, paymentMethodId } });

      if (paymentFlow.is.any('gc-lookup')) {
        paymentFlow.do('gc-balance', gcBalResp);
        paymentFlow.do('gc-lookup-end');
        modal.giftCardBalanceApply.open();
        return;
      } else {
        if (gcBalResp.balance === 0) throw new ServiceError({ message: t('giftCard.errors.zeroBalance') });
      }

      let nextTotal = 0;
      if (paymentFlow.is.part) {
        nextTotal = paymentFlow.part.amount;
      } else if (paymentFlow.is.split) {
        nextTotal = paymentFlow.split.amount;
      } else {
        nextTotal = check?.balance.unpaid ?? 0;
      }

      debitGiftCardRequest = {
        account,
        cardEntryMethod: 'CardSwipe' as DebitGiftCardRequest['cardEntryMethod'],
        checkId,
        paymentMethodId,
        tip: 0,
        total: nextTotal,
        transactionNumber: paymentFlow.gc.transactionNumber,
      };
    } catch (error) {
      // TODO: explore something safer
      // !!! CAUTION: turning the card reader back on after an error could have side effects
      //  - i.e. - flow has turned off the card reader => this catch happens => card reader is active when it should not be
      cardReader.turn.on('giftCardPayment error');
      cardReader.reinit('giftCardPayment error');
      toast.show.serviceError(error, { id: `${sectionId}.giftCardPayment` });
    }

    try {
      if (!debitGiftCardRequest) return;
      const response = await giftCardDebit.mutateAsync({ debitGiftCardRequest }, {
        onSettled: () => paymentFlow.do('gc-transaction-id'),
      });
      if (!response) {
        log.error('cardReader.turn.onRead', 'gc', 'no response received from initiatePayment');
        cardReader.reinit('giftCardPayment !response');
        return;
      }
      // always update the check
      // - check.payment needs to be applied to current check
      // - if payment failed, new check activity is required
      void currentCheck.query.refetch();

      if (response.payment.storeAndForward) {
        document.dispatchEvent(new CustomEvent('notify.CheckPaymentGoodFaithTransaction', {
          detail: {
            id: `${uniqueInt()}`,
            checkId: check?.id,
            employeeId: currentOperator.employeeId,
            payment: response.payment,
          },
        }));
      }

      if (response?.payment.paymentStatus === 'Success' || response?.payment.paymentStatus === 'PreAuth') {
        // Print Payment receipt on success
        // Print request gets routed to the terminal-assigned printer
        void printerSend.mutateAsync({ type: 'Payment', checkId, paymentId: response.payment.id });
        paymentFlow.do('gc-pay', { checkId, payment: response.payment });
        modal.execute.close('on-next');
        modal.payComplete.open('gc');
      } else if (response?.payment.paymentStatus === 'Declined') {
        const { title, message } = paymentResponseDeclined.parse(response.payment);
        toast.show.error({ id: response.payment.id, title, message });
        cardReader.reinit('giftCardPayment response status declined');
      }
    } catch (error) {
      onComplete({ intent: 'error', error });
    }
  };

  const onPreAuthPayment = (options: { payment: Check['payments'][number], amount: number }) => {
    if (typeof checkId !== 'number') return log.debug('onPreAuthPayment', '!checkId');

    adjust.mutate({
      payment: {
        checkId,
        paymentId: options.payment.id,
        tip: 0,
        total: options.amount,
        // can only mutate preAuth once
        // - by passing in false for "isOpenTab", we are turning off the bar tab mode
        isOpenTab: false,
      }
    }, {
      onSuccess: async (response) => {
        if (response.payment.paymentStatus === 'Declined') {
          toast.showAndAutoHide.warning({
            id: `${sectionId}.onPreAuthPayment.Declined`,
            title: t('payment.labels.declined'),
            message: t('payment.labels.paymentAdjustmentDeclined'),
          });
          return;
        }
        void currentCheck.query.refetch();
        paymentFlow.do('preAuth-adjust', { payment: response.payment });
        modal.execute.close('on-next');
        if (terminalSettings?.digitalSignatureEnabled) {
          flipAndTip.navigate({ checkId, paymentId: response.payment.id });
        } else {
          modal.payComplete.open('preAuth');
        }
      },
      onError: (error) => {
        toast.show.serviceError(error, { id: `${sectionId}.onPay` });
      },
    });
  };

  const creditCardPayment = async (options?: { paymentMethodId?: number; duplicateOK?: boolean }) => {
    if (typeof checkId !== 'number') return log.debug('creditCardPayment', '!checkId');

    if (ccPaymentMethodOptions.length < 1) {
      currentPayment.setStatus('No cc payment methods set up to work with card reader...');
      cardReader.reinit('creditCardPayment !ccPaymentMethodOptions');
      return;
    }

    let nextTotal = 0;
    if (paymentFlow.is.part) {
      nextTotal = paymentFlow.part.amount;
    } else if (paymentFlow.is.split) {
      nextTotal = paymentFlow.split.amount;
    } else {
      nextTotal = check?.balance.unpaid ?? 0;
    }

    // check if the current card was already used on the check
    const cardPreviouslyUsed = duplicateCardVerify(currentCheck.query.data?.payments, cardReader.read?.pax);
    if (!ref.current.duplicateCardDetails) {
      ref.current.duplicateCardDetails = {
        cardBrand: cardReader.read?.pax?.cardBrand ?? '',
        lastFour: cardReader.read?.pax?.cardLast4Pan ?? '',
        prevAmount: cardPreviouslyUsed?.amountAppliedToCheck ?? 0,
        nextAmount: nextTotal,
      };
    }
    // if the current card was previously used and the user has yet confirmed, open the duplicate card modal
    if (!!cardPreviouslyUsed && !options?.duplicateOK) return modal.duplicateCard.open();

    modal.duplicateCard.close('on-user-confirm');

    // We do not have a payment method id when getting card reader swipe
    if (ccPaymentMethodOptions.length > 1 && !options?.paymentMethodId) return modal.ccMethod.open();

    modal.ccMethod.close('on-option-selected');

    const paymentMethodId = options?.paymentMethodId ?? ccPaymentMethodOptions[0].value;

    const transaction = new CardReaderRequest({
      paymentTypeId: paymentMethodId,
      checkId,
      tip: 0,
      total: nextTotal,
      cardReaderType: CardReaderType.pax,
      paxData: cardReader.read?.pax as unknown as PaxDataDef & { giftcardNumber?: string },
    });

    try {
      const checkRevenueCenter = revenueCenters.data?.find(({ id }) => id === check?.revenueCenterId);
      // digital signature being enabled should always use "preAuth" as we are mutating the payment w/ a tip
      // TODO: "allowCcTipAdjust" on the revenue center is a confusing setting and a revision for this setting is being written up
      const requester = terminalSettings?.digitalSignatureEnabled || checkRevenueCenter?.allowCcTipAdjust
        ? preAuth
        : sale;
      const response = await requester.mutateAsync({ transaction });
      Object.assign(response?.payment.ccData ?? {}, {
        cardBrand: cardReader.read?.pax.cardBrand,
        lastFourCcNumber: cardReader.read?.pax.cardLast4Pan,
      });

      if (!response) {
        log.error('creditCardPayment', '!response');
        cardReader.reinit('creditCardPayment !response');
        return;
      }

      ref.current.duplicateCardDetails = undefined;

      // always update the check
      // - check.payment needs to be applied to current check
      // - if payment failed, new check activity is required
      void currentCheck.query.refetch();

      if (response.payment.storeAndForward) {
        document.dispatchEvent(new CustomEvent('notify.CheckPaymentGoodFaithTransaction', {
          detail: {
            id: `${uniqueInt()}`,
            checkId: check?.id,
            employeeId: currentOperator.employeeId,
            payment: response.payment,
          },
        }));
      }

      if (response.payment.paymentStatus === 'Success' || response?.payment.paymentStatus === 'PreAuth') {
        // Print Payment receipt on success
        // Print request gets routed to the terminal-assigned printer
        void printerSend.mutateAsync({ type: 'Payment', checkId, paymentId: response.payment.id });
        paymentFlow.do('cc-pay', { payment: response.payment });
        const paymentId = response.payment.id;

        modal.execute.close('on-next');
        if (paymentFlow.is.part) {
          if (terminalSettings?.digitalSignatureEnabled) {
            flipAndTip.navigate({ checkId, paymentId });
          } else {
            modal.payComplete.open('cc');
          }
        } else if (paymentFlow.is.split) {
          if (terminalSettings?.digitalSignatureEnabled) {
            flipAndTip.navigate({ checkId, paymentId });
          } else {
            modal.payComplete.open('cc');
          }
        } else {
          if (terminalSettings?.digitalSignatureEnabled) {
            flipAndTip.navigate({ checkId, paymentId });
          } else {
            modal.payComplete.open('cc');
          }
        }
      } else if (response?.payment.paymentStatus === 'Declined') {
        const { title, message } = paymentResponseDeclined.parse(response.payment);
        toast.show.error({ id: response.payment.id, title, message });
        cardReader.turn.on('creditCardPayment status declined');
        cardReader.reinit('creditCardPayment status declined');
      }
    } catch (error) {
      onComplete({ intent: 'error', error });
    }
  };

  const cardReader = useCardReaderControl({
    onRead: async ({ read }) => {
      if (grantedPermissions.giftCardPayment.isAllowed && cardReader.is.giftCard(read.pax) && !cardReader.is.giftCardInvalid(read.pax)) {
        void giftCardPayment();
      } else if (grantedPermissions.ccPayment.isAllowed) {
        void creditCardPayment();
      }
    },
  });

  // TODO: isolate modal logic into their own hooks
  const modal = {
    changeDue: {
      ...useModalControl({
        onOpen: () => cardReader.turn.off('modal.changeDue.onOpen'),
        onClose: () => {
          cardReader.turn.on('modal.changeDue.onClose');
          onComplete({ intent: 'modal' });
        },
      }),
      onVoidClick: () => voidPayment(),
      onReceiptClick: () => void printPaymentReceipt(paymentFlow.pay.id as string),
      changeDue: paymentFlow.cash.changeDue,
      check,
      loading: currentCheck.query.isFetching,
    },
    customCashAmount: {
      ...useModalControl({
        onOpen: () => cardReader.turn.off('modal.customCashAmount.onOpen'),
        onClose: () => cardReader.turn.on('modal.customCashAmount.onClose'),
      }),
      onNext: (amount: number) => {
        modal.customCashAmount.close();
        void cashPayment(amount);
      },
    },
    otherPayment: {
      ...useModalControl({
        onOpen: () => cardReader.turn.off('modal.otherPayment.onOpen'),
        onClose: () => cardReader.turn.on('modal.otherPayment.onClose'),
      }),
      onNext: (...args: Parameters<typeof otherPayment>) => {
        otherPayment(...args);
      },
    },
    part: {
      ...useModalControl<{ Close: 'on-user-cancel' | 'on-next' }>({
        onOpen: () => cardReader.turn.off('modal.part.onOpen'),
        onClose: (intent) => {
          // do nothing as we want to persist state for the next modal to pick up on
          if (intent === 'on-next') return; //log.debug('modal.part.onClose', 'intent:', intent);
          if (intent === 'on-user-cancel') {
            // go back to unpaid state
            cardReader.turn.on('modal.part.onClose on-user-cancel');
            paymentFlow.reset();
          }
        },
      }),
      onNext: (amount: number) => {
        paymentFlow.do('part-init', amount);
        modal.part.close('on-next');
        modal.execute.open();
      },
      check,
    },
    execute: {
      ...useModalControl<{ Close: 'on-user-cancel' | 'on-next' | 'on-error' }>({
        onOpen: () => cardReader.turn.on('modal.execute.onOpen'),
        onClose: (intent) => {
          // do nothing as we want to persist state for the next modal to pick up on
          if (intent === 'on-next') return; // log.debug(`modal.execute.onClose intent: ${intent}`);
          // open modal again to
          if (intent === 'on-error') return modal.execute.open();
          // go back to unpaid state
          if (intent === 'on-user-cancel') {
            paymentFlow.reset();
            cardReader.turn.on(`modal.execute.onClose intent: ${intent}`);
          }
        },
      }),
      onPaymentTypeClick: (options: ExecuteModalActionOption) => {
        const { type } = options;
        switch (type) {
          case 'preAuth': {
            const { payment, amount } = options;
            onPreAuthPayment({ payment, amount });
            break;
          }
          case 'manual': {
            const { amount } = options;
            void manualCreditCardPayment({ amount });
            break;
          }
          case 'gc': {
            paymentFlow.do('gc-lookup-start');
            break;
          }
          case 'cash': {
            modal.customCashAmount.open();
            break;
          }
          case 'other': {
            modal.otherPayment.open();
            break;
          }
        }
      }
    },
    split: {
      ...useModalControl<{ Close: 'on-user-cancel' | 'on-next' }>({
        onOpen: () => cardReader.turn.off(`modal.split.onOpen`),
        onClose: (intent) => {
          // do nothing as we want to persist state for the next modal to pick up on
          if (intent === 'on-next') return; // log.debug('modal.split.onClose', 'intent:', intent);
          if (intent === 'on-user-cancel') {
            // go back to unpaid state
            paymentFlow.reset();
            cardReader.turn.on(`modal.split.onClose intent: ${intent}`);
          }
        },
      }),
      onNext: async (splitBy: number) => {
        if (typeof checkId !== 'number') return log.debug('modal.split.onNext', '!checkId');
        try {
          const response = await balanceSplits.mutateAsync({ checkId, splitBy });
          paymentFlow.do('split-init', response);
          modal.split.close('on-next');
          modal.execute.open();
        } catch (err) {
          toast.show.serviceError(err, { id: `${sectionId}.modal.split.onNext`, title: t('service.Check.error.balanceSplits') });
        }
      },
    },
    payComplete: {
      ...useModalControl<{ Close: 'on-user-cancel' | 'on-void' | 'on-receipt'; Open: 'cc-manual' | 'gc' | 'cc' | 'cash' | 'other' | 'preAuth' }>({
        onOpen: (intent) => {
          cardReader.turn.off(`modal.payComplete.onOpen intent: ${intent}`);
          if (paymentFlow.is.split) {
            paymentFlow.do('split-next');
          }
        },
        onClose: (intent) => {
          cardReader.turn.on(`modal.payComplete.onClose intent: ${intent}`);
          if (paymentFlow.has.split && intent !== 'on-void') {
            modal.execute.open();
          } else {
            onComplete({ intent: 'modal' });
          }
        },
      }),
      onReceiptClick: (paymentId: string) => {
        modal.payComplete.close('on-receipt');
        void printPaymentReceipt(paymentId);
      },
      onVoidClick: () => {
        if (paymentFlow.is.gc) void voidGiftCardDebit();
        else void voidPayment();
      },
    },
    ccMethod: {
      ...useModalControl<{ Close: 'on-user-cancel' | 'on-option-selected' }>({
        onOpen: () => cardReader.turn.off('modal.ccMethod.onOpen'),
        onClose: (intent) => {
          // don't reinit card readers unless a user driven cancel/close (when the modal is closed via X or backdrop click)
          if (intent === 'on-user-cancel') cardReader.turn.on(`modal.ccMethod.onClose intent: ${intent}`);
        }
      }),
      title: t('payment.action.selectCCMethod'),
      options: ccPaymentMethodOptions,
      onOptionClick: async (option: { value: number }) => {
        if (!cardReader.read) return log.error('ccMethod.onOptionClick', 'no read available');
        // checking that ref.current.duplicateCardDetails is populated prevents the user from getting stuck in a loop when a duplicate card is used and they need to select a payment method
        void creditCardPayment({ paymentMethodId: option.value, duplicateOK: !!ref.current.duplicateCardDetails });
      },
    },
    giftCardMethodPayment: {
      ...useModalControl<{ Close: 'on-user-cancel' | 'on-option-selected' }>({
        onOpen: () => cardReader.turn.off(`modal.giftCardMethodPayment.onOpen`),
        onClose: (intent) => {
          // don't reinit card readers unless a user driven cancel/close (when the modal is closed via X or backdrop click)
          if (intent === 'on-user-cancel') cardReader.turn.on(`modal.giftCardMethodPayment.onClose intent: ${intent}`);
        }
      }),
      title: t('payment.action.selectGCMethod'),
      options: gcPaymentMethodOptions,
      onOptionClick: async (option: { value: number }) => {
        const paymentMethodId = option.value;
        if (modal.giftCardBalanceApply.ref.current.isOpen) {
          void onApplyGiftCardToCheck({
            from: 'modal.giftCardMethodPayment.onOptionClick',
            accountNumber: paymentFlow.gc.accountNumber,
            paymentMethodId,
          });
        } else {
          if (!cardReader.read) return log.error('giftCardMethodPayment.onOptionClick', 'no read available');
          const isGiftCard = cardReader.read.pax.giftcardData !== undefined && cardReader.read.pax.giftcardData.number !== undefined && cardReader.read.pax.giftcardData.number !== '';
          if (!isGiftCard) return log.error('giftCardMethodPayment.onOptionClick', '!isGiftCard');
          const isValidGiftCard = cardReader.read.pax.giftcardData.number.toLowerCase() !== 'invalid';
          if (!isValidGiftCard) return log.error('giftCardMethodPayment.onOptionClick', '!isValidGiftCard');
          void giftCardPayment({ paymentMethodId });
        }
      },
    },
    giftCardMethodLookup: {
      ...useModalControl<{ Close: 'on-user-cancel' | 'on-option-selected' }>({
        onOpen: () => cardReader.turn.off(`modal.giftCardMethodLookup.onOpen`),
        onClose: (intent) => {
          // don't reinit card readers unless a user driven cancel/close (when the modal is closed via X or backdrop click)
          if (intent === 'on-user-cancel') cardReader.turn.on(`modal.giftCardMethodLookup.onClose intent: ${intent}`);
        }
      }),
      title: t('payment.action.selectGCMethod'),
      options: gcPaymentMethodOptions,
      onOptionClick: (option: { value: number }) => {
        void onGiftCardBalanceLookup({
          from: 'modal.giftCardMethodLookup.onOptionClick',
          paymentMethodId: option.value,
          accountNumber: paymentFlow.gc.accountNumber,
        });
      },
    },
    giftCardLookup: {
      ...useCheckPaymentModal({
        // FYI: internal card reader from modal will take over the stack
        // - block other card reads while open
        onOpen: () => cardReader.turn.off(`modal.giftCardLookup.onOpen`),
        onClose: () => {
          paymentFlow.do('gc-lookup-end');
          cardReader.turn.on(`modal.giftCardLookup.onClose`);
        },
        isOpen: paymentFlow.is.any('gc-lookup'),
      }),
      onLookupCardRead: (read: CardReadSuccessOptions) => {
        // TODO: why do we need a separate card reader for gift card lookup
        //  - add documentation
        //  - does "mode:'msr', amount:0" differ from "mode:'emv', amount:number"
        cardReader.read = read;
        void onGiftCardBalanceLookup({ from: 'modal.giftCardLookup.onLookupCardRead' });
      },
      onManualAccountLookup: (accountNumber: string) => {
        void onGiftCardBalanceLookup({ from: 'modal.giftCardLookup.onManualAccountLookup', accountNumber });
      }
    },
    giftCardBalanceApply: {
      ...useModalControl({
        onOpen: () => cardReader.turn.off(`modal.giftCardBalanceApply.onOpen`),
        onClose: () => cardReader.turn.on(`modal.giftCardBalanceApply.onClose`),
      }),
      onApplyClick: () => {
        void onApplyGiftCardToCheck({
          from: 'modal.giftCardBalanceApply.onApplyClick',
          accountNumber: paymentFlow.gc.accountNumber,
          paymentMethodId: paymentFlow.gc.paymentMethodId,
        });
      },
    },
    manualMethod: {
      ...useModalControl<{ Close: 'on-user-cancel' | 'on-option-selected' }>({
        onOpen: () => cardReader.turn.off(`modal.manualMethod.onOpen`),
        onClose: (intent) => {
          // don't reinit card readers unless a user driven cancel/close (when the modal is closed via X or backdrop click)
          if (intent === 'on-user-cancel') cardReader.turn.on(`modal.manualMethod.onClose intent: ${intent}`);
        }
      }),
      title: t('payment.action.selectCCMethod'),
      options: manualCCMethodOptions,
      onOptionClick: (option: { value: number; }) => {
        void manualCreditCardPayment({ paymentMethodId: option.value });
      },
    },
    duplicateCard: {
      ...useModalControl<{ Close: 'on-user-cancel' | 'on-user-confirm' }>({
        onOpen: () => cardReader.turn.off('modal.duplicateCard.onOpen'),
        onClose: (intent) => {
          // don't reinit card readers unless a user driven cancel/close (when the modal is closed via X or backdrop click)
          if (intent === 'on-user-cancel') cardReader.turn.on(`modal.duplicateCard.onClose intent: ${intent}`);
        },
      }),
      onConfirm: () => {
        void creditCardPayment({ duplicateOK: true });
      },
      details: ref.current.duplicateCardDetails,
    },
  };

  const onComplete = async (options: PaymentCompleteOptions) => {
    if (!checkId) return log.debug('onComplete', `intent: ${options.intent}`, '!checkId');
    switch (options.intent) {
      case 'error': {
        const { error } = options;
        const { message, messagePoints } = parseError(error);
        if (cardReader.is.resultCode.cancelled(message)) {
          // don't show error toast if error caused by cancelling the card reader Tx
          log.debug('onComplete', `intent: ${options.intent}`, 'block extra toast from user when canceled and a route change or a new emv was initiated');
          return;
        }
        log.debug('onComplete', `intent: ${options.intent}`, { error, balance: check?.balance });
        modal.execute.close('on-error');
        toast.show.error({ id: `${sectionId}.onCardReaderPaymentRequest`, title: t('labels.attention'), message, messagePoints });
        break;
      }
      case 'modal': {
        // cache "paymentId" as the payment flow will reset
        //  - also, "paymentId" used in toast click action that could happen sometime later
        const paymentId = paymentFlow.pay?.id ? `${paymentFlow.pay.id}` : undefined;
        const response = await currentCheck.query.refetch();
        // granular reset of payment flow
        paymentFlow.do('on-complete-modal');
        if (response.data?.balance.unpaid === 0) {
          toast.showAndAutoHide.warning({
            id: `${sectionId}.paymentComplete`,
            title: t('payment.labels.checkHasBeenPaid', { checkLabel: response.data?.label }),
            action: (
              <CTA
                label={t('labels.reprintReceipts')}
                variant="outlined"
                color="inherit"
                onClick={() => {
                  if (typeof paymentId !== 'string') return log.debug('toast.reprintReceipts.onClick', '!paymentId');
                  printPaymentReceipt(paymentId);
                }}
              />
            ),
          });
        }
        if (response.data?.status === 'Closed') {
          toast.showAndAutoHide.warning({
            id: `${sectionId}.checkClosed`,
            title: t('check.labels.checkClosed', { checkLabel: response.data?.label }),
            action: (
              <CTA
                label={t('labels.reprintReceipts')}
                variant="outlined"
                color="inherit"
                onClick={printGuestReceipt}
              />
            )
          });
          navigate(crumbs.pos.checks.main.interpolate({ tabId: route.params.value.defaultView }));
        }
        break;
      }
    }
  };

  useDOMEventListener('notify.CheckUpdated', (evt) => {
    if (evt.detail.check.status !== 'Opened') windowCtrl.close();
  });

  const windowCtrl = useManualPaymentWindow({
    id: sectionId,
    open: {
      url: '/pay.html',
      target: 'Payment Screen',
      features: 'popup',
    },
    onMessage: async (message) => {
      if (typeof checkId !== 'number') {
        log.debug('onWindowMessage', '!checkId');
        return;
      }

      if (message.type === 'window' && message.status === 'unexpected-close') {
        log.debug('onWindowMessage', 'message.status:', message.status, 'Manual CC Payment Unexpected Close!');
        cardReader.turn.on(`windowCtrl unexpected-close`);
        // !!! only want to reset during unpaid so we don't blow out "split" or "part" flow state
        if (paymentFlow.is.unpaid) paymentFlow.reset();
        return;
      }

      if (message.status === 'error') {
        log.error('onWindowMessage', message);
        cardReader.turn.on(`windowCtrl status error`);
        toast.show.error({
          id: `${sectionId}-manual-cc-error`,
          message: t('payment.info.manualError'),
        });
        // !!! only want to reset during unpaid so we don't blow out "split" or "part" flow state
        if (paymentFlow.is.unpaid) paymentFlow.reset();
        return;
      }

      if (message.type === 'epx' && message.status === 'pay-complete') {
        document.dispatchEvent(new CustomEvent('TerminalUiChangeTracker.autoLogOut.override', { detail: { isPause: false, reason: 'manual CC flow' } }));
        currentPayment.epx.status = message.data;
        windowCtrl.close();
        const { status } = currentPayment.epx.status;
        // modal clean up
        modal.execute.close('on-next');
        if (status === 'approved') {
          void printerSend.mutateAsync({ type: 'Payment', checkId, paymentId: currentPayment.epx.response?.paymentId });
          modal.payComplete.open('cc-manual');
        } else if (status === 'declined') {
          toast.show.error({
            id: `${sectionId}-manual-cc-declined`,
            message: t('payment.info.cardDeclined'),
          });
        }
      }
    },
  });

  const manualCreditCardPayment = async (options?: { paymentMethodId?: number, amount?: number }) => {
    cardReader.turn.off(`manualCreditCardPayment`);
    if (typeof checkId !== 'number') return log.debug('manualPaymentRequest', '!checkId');
    // stash manual payment amt in case we have
    // a secondary flow (pick pay method, auth request) kick off
    if (!ref.current.manualCCAmount) ref.current.manualCCAmount = options?.amount;

    // nothing we can do
    if (manualCCMethodOptions.length < 1) return;

    if (manualCCMethodOptions.length > 1 && !options?.paymentMethodId) return modal.manualMethod.open();

    modal.manualMethod.close('on-option-selected');

    // we need to include tip into total amount. API expects total amount to include tip
    const amount = options?.amount ? options?.amount : ref.current.manualCCAmount ?? check?.balance.unpaid ?? 0;

    const paymentMethodId = options?.paymentMethodId ?? manualCCMethodOptions[0].value;

    const payload: ManualCCTransactionRequest = {
      paymentTypeId: paymentMethodId,
      checkId,
      tip: 0,
      total: amount,
      // per product, manual payment is always to be considered "card not present" because there is no way to prove the card was present
      isCardPresent: false,
      isOpenTab: false,
    };

    try {
      log.debug('manualPaymentRequest', { payload });
      const checkRevenueCenter = revenueCenters.data?.find(({ id }) => id === check?.revenueCenterId);
      const response = checkRevenueCenter?.allowCcTipAdjust
        ? await epxPreAuthManual.mutateAsync({ manual: payload })
        : await epxSaleManual.mutateAsync({ manual: payload });
      if (response) {
        ref.current.manualCCAmount = undefined;
        // per product, industry should always be 'M' because manual payments should always be considered "card not present"
        const respWithIndustry = Object.assign(response, { industry: EPXIndustry.mobile });
        // store requested amounts in the case of a partial approval
        currentPayment.epx.response = respWithIndustry;
        paymentFlow.do('cc-manual-pay', {
          payment: {
            id: currentPayment.epx.response?.paymentId as string,
          },
        });
        log.debug('manualPaymentRequest', { respWithIndustry });
        const message: ManualPaymentMessage = { type: 'epx', status: 'pay', data: respWithIndustry };
        document.dispatchEvent(new CustomEvent('TerminalUiChangeTracker.autoLogOut.override', { detail: { isPause: true, reason: 'manual CC flow' } }));
        windowCtrl.open(message);
      }
    } catch (err) {
      toast.show.serviceError(err, { id: `${sectionId}.manualPayment` });
    }
  };

  const cashPayment = async (amount: number) => {
    cardReader.turn.off(`cashPayment`);
    if (createCash.isPending) return;
    if (typeof checkId !== 'number') return log.debug('cashPayment', '!checkId');

    // !!! first cash payemnt is autopopulated from the backend
    const firstCashMethodId = cashPaymentMethods.data?.[0]?.id;
    if (typeof firstCashMethodId !== 'number') return log.debug('cashPayment', '!firstCashMethodId');

    try {
      const response = await createCash.mutateAsync({
        payment: {
          checkId,
          paymentId: Uuid.new(),
          total: amount,
          tip: 0,
          basis: paymentFlow.basis,
        },
        paymentMethodId: firstCashMethodId,
      });
      if (response) {
        paymentFlow.do('cash-pay', { payment: response.payment, amount });
        modal.execute.close('on-next');
        if (paymentFlow.is.split) {
          modal.payComplete.open('cash');
        } else {
          modal.changeDue.open();
        }
        void cashDrawer.popDrawer();
        void currentCheck.query.refetch();
      }
    } catch (err) {
      toast.show.serviceError(err, { id: `${sectionId}.cashPayment`, title: t('payment.error.createCash') });
    }
  };

  const otherPayment = (args: { amount: number, paymentMethodId: number }) => {
    if (typeof checkId !== 'number') return log.debug('otherPayment', '!checkId');
    const pm = paymentMethodMap.data?.get(args.paymentMethodId);
    if (pm?.type === 'Cash') {
      createCash.mutate({
        payment: {
          checkId,
          paymentId: Uuid.new(),
          total: args.amount,
          tip: 0,
          basis: paymentFlow.basis,
        },
        paymentMethodId: args.paymentMethodId,
      }, {
        onSuccess: (response) => {
          paymentFlow.do('cash-pay', { payment: response.payment, amount: args.amount });
          modal.execute.close('on-next');
          if (paymentFlow.is.split) {
            modal.payComplete.open('cash');
          } else {
            modal.changeDue.open();
          }
          void cashDrawer.popDrawer();
          void currentCheck.query.refetch();
        },
        onError: (e) => {
          toast.show.serviceError(e, { id: `${sectionId}.otherPayment`, title: t('payment.error.otherPayment') });
        },
      });
    } else {
      other.mutate({
        payment: {
          checkId,
          paymentId: Uuid.new(),
          total: args.amount,
          tip: 0,
          basis: paymentFlow.basis,
        },
        paymentMethodId: args.paymentMethodId,
      }, {
        onSuccess: (response) => {
          paymentFlow.do('other-pay', { payment: response.payment });
          modal.execute.close('on-next');
          modal.payComplete.open('other');
          void currentCheck.query.refetch();
        },
        onError: (e) => {
          toast.show.serviceError(e, { id: `${sectionId}.otherPayment`, title: t('payment.error.otherPayment') });
        },
      });
    }
  };

  const voidPayment = async () => {
    try {
      if (!checkId) return log.debug('voidPayment', '!checkId');
      if (check?.status === 'Closed') await reopenCheck();
      if (!paymentFlow.pay?.id) return log.debug('voidPayment', '!payment.id');

      const payment = check?.payments.find(({ id }) => id === paymentFlow.pay.id);
      if (!payment) return log.debug('voidPayment', '!payment');

      const response = await removePayment.mutateAsync({
        payment: {
          basis: payment.basis,
          checkId,
          digitalSignature: payment.digitalSignature,
          isOpenTab: payment.ccData?.isOpenTab,
          paymentId: payment.id,
          tip: payment.tip,
          total: payment.total,
        },
      });
      if (response.payment.paymentStatus === 'Declined') {
        return toast.showAndAutoHide.warning({
          id: `${sectionId}.deletePayment`,
          title: t('payment.labels.declined'),
          message: t('payment.labels.removePaymentDeclined'),
        });
      }
      toast.showAndAutoHide.warning({
        id: `${sectionId}.paymentVoided`,
        title: t('payment.labels.paymentVoided'),
      });
      void currentCheck.query.refetch();
    } catch (err) {
      toast.show.serviceError(err, { id: `${sectionId}.deletePayment` });
    } finally {
      // !!! "void" will cancel out of all flows
      paymentFlow.reset();
      if (modal.changeDue.ref.current.isOpen) modal.changeDue.close();
      if (modal.payComplete.ref.current.isOpen) modal.payComplete.close('on-void');
      cardReader.turn.on(`voidPayment.finally`);
    }
  };

  const voidGiftCardDebit = async () => {
    try {
      if (check?.status === 'Closed') await reopenCheck();
      if (!paymentFlow.gc.request) return log.debug('voidGiftCardDebit', '!giftCard.paymentReq');
      const response = await giftCardVoidDebit.mutateAsync({ voidDebitGiftCardRequest: paymentFlow.gc.request }, {
        onSettled: () => paymentFlow.do('gc-transaction-id'),
      });
      if (response.payment.paymentStatus === 'Declined') {
        return toast.showAndAutoHide.warning({
          id: `${sectionId}.voidGiftCardDebit.declined`,
          title: t('payment.labels.declined'),
          message: t('payment.labels.voidGiftCardDebitDeclined'),
        });
      }
    } catch (err) {
      toast.show.serviceError(err, { id: `${sectionId}.voidGiftCardDebit`, title: t('giftCard.errors.voidDebit') });
    } finally {
      // "void" will cancel out of all flows
      paymentFlow.reset();
      if (modal.payComplete.ref.current.isOpen) modal.payComplete.close('on-void');
      cardReader.turn.on(`voidGiftCardDebit.finally`);
    }
  };

  const reopenCheck = async () => {
    if (typeof checkId !== 'number') return log.debug('reopenCheck', '!checkId');
    try {
      await updateStatus.mutateAsync({ checkId, status: 'open' });
      await currentCheck.query.refetch();
    } catch (err) {
      toast.show.serviceError(err, { id: `${sectionId}.reopenCheck`, title: t('check.errors.reopenOne') });
    }
  };

  const printGuestReceipt = async () => {
    if (typeof checkId !== 'number') return log.debug('printGuestReceipt', '!checkId');
    await printerSend.mutateAsync({ type: 'Guest', checkId });
  };

  const printPaymentReceipt = async (paymentId: string) => {
    if (typeof checkId !== 'number') return log.debug('printPaymentReceipt', '!checkId');
    await printerSend.mutateAsync({ type: 'Payment', checkId, paymentId });
    if (modal.changeDue.ref.current.isOpen) modal.changeDue.close();
  };

  const onGiftCardBalanceLookup = async (options: { accountNumber?: string; paymentMethodId?: number; from: string; }) => {
    const accountNumber = options.accountNumber ?? cardReader.read?.pax.giftcardData.number;
    paymentFlow.do('gc-account-number', accountNumber);

    if (options.paymentMethodId) paymentFlow.do('gc-payment-method-id', options.paymentMethodId);

    const payMethodWithMatchingMerchId = (gcPaymentMethods.data ?? [])
      .find((pm) => pm.docsOnlyFieldsGiftCard?.merchantId === cardReader.read?.pax.giftcardData.merchId);

    if (gcPaymentMethodOptions.length < 1) {
      return toast.show.error({
        id: `${sectionId}.onApplyGiftCardToCheck`,
        title: t('giftCard.labels.lookup'),
        message: t('giftCard.labels.noGiftCardPaymentMethods'),
      });
    }

    if (gcPaymentMethodOptions.length > 1 && !options?.paymentMethodId && !payMethodWithMatchingMerchId) return modal.giftCardMethodLookup.open();

    modal.giftCardMethodLookup.close('on-option-selected');

    try {
      if (typeof accountNumber !== 'string') return log.error('onGiftCardBalanceLookup', '!accountNumber');

      const response = await giftCardBalance.mutateAsync({
        giftCardBalanceRequest: {
          account: accountNumber,
          paymentMethodId: payMethodWithMatchingMerchId?.id ?? options?.paymentMethodId ?? gcPaymentMethodOptions[0].value,
        },
      });
      paymentFlow.do('gc-balance', response);
      paymentFlow.do('gc-lookup-end');
      modal.giftCardBalanceApply.open();
    } catch (err) {
      paymentFlow.do('gc-payment-method-id', undefined);
      toast.show.serviceError(err, { id: `${sectionId}.onGiftCardBalanceLookup` });
      cardReader.reinit('onGiftCardBalanceLookup error');
    }
  };

  const onApplyGiftCardToCheck = async (options: { accountNumber?: string; paymentMethodId?: number; from: string; }) => {
    if (typeof checkId !== 'number') return log.debug('onApplyGiftCardToCheck', '!checkId');

    if (gcPaymentMethodOptions.length < 1) {
      return toast.show.error({
        id: `${sectionId}.onApplyGiftCardToCheck`,
        title: t('giftCard.labels.applyToCheck'),
        message: t('giftCard.labels.noGiftCardPaymentMethods'),
      });
    }

    if (gcPaymentMethodOptions.length > 1 && !options.paymentMethodId) return modal.giftCardMethodPayment.open();

    modal.giftCardMethodPayment.close('on-option-selected');

    try {
      if (typeof options.accountNumber !== 'string') return log.error('onApplyGiftCardToCheck', '!accountNumber');

      let nextTotal = 0;
      if (paymentFlow.is.part) {
        nextTotal = paymentFlow.part.amount;
      } else if (paymentFlow.is.split) {
        nextTotal = paymentFlow.split.amount;
      } else {
        nextTotal = check?.balance.unpaid ?? 0;
      }

      const response = await giftCardDebit.mutateAsync({
        debitGiftCardRequest: {
          account: options.accountNumber,
          cardEntryMethod: 'Manual' as DebitGiftCardRequest['cardEntryMethod'],
          checkId,
          paymentMethodId: options.paymentMethodId ?? gcPaymentMethodOptions[0].value,
          tip: 0,
          total: nextTotal,
          transactionNumber: paymentFlow.gc.transactionNumber,
        },
      }, {
        onSettled: () => paymentFlow.do('gc-transaction-id'),
      });
      if (response?.payment.paymentStatus === 'Success' || response?.payment.paymentStatus === 'PreAuth') {
        // Print Payment receipt on success
        // Print request gets routed to the terminal-assigned printer
        void printerSend.mutateAsync({ type: 'Payment', checkId, paymentId: response.payment.id });
        paymentFlow.do('gc-pay', { checkId, payment: response.payment });
        void currentCheck.query.refetch();
        modal.giftCardBalanceApply.close();
        modal.execute.close('on-next');
        modal.payComplete.open('gc');
      } else if (response?.payment.paymentStatus === 'Declined') {
        toast.showAndAutoHide.warning({
          id: `${sectionId}.onApplyGiftCardToCheck`,
          title: t('payment.labels.declined'),
          message: t('payment.labels.giftCardDebitDeclined'),
        });
      }
    } catch (err) {
      toast.show.serviceError(err, { id: `${sectionId}.onApplyGiftCardToCheck` });
    } finally {
      paymentFlow.do('gc-payment-method-id', undefined);
    }
  };

  const availableCashPaymentMethods = useMemo(() => (
    (cashPaymentMethods.data ?? []).filter((pm) => grantedPermissions.cashPayment.paymentMethodIds.includes(pm.id))
  ), [cashPaymentMethods.data, grantedPermissions.cashPayment.paymentMethodIds]);

  const canDoGiftCardPayment = useMemo(() => {
    // must be allowed AND have valid payment methods
    if (!grantedPermissions.giftCardPayment.isAllowed) return false;
    if (!grantedPermissions.giftCardPayment.paymentMethodIds.length) return false;
    return true;
  }, [grantedPermissions.giftCardPayment.isAllowed, grantedPermissions.giftCardPayment.paymentMethodIds.length]);

  const canDoCCPayment = useMemo(() => {
    // must be allowed AND have valid payment methods
    if (!grantedPermissions.ccPayment.isAllowed) return false;
    if (!grantedPermissions.ccPayment.paymentMethodIds.length) return false;
    return true;
  }, [grantedPermissions.ccPayment.isAllowed, grantedPermissions.ccPayment.paymentMethodIds.length]);

  const canDoCardReaderPayment = useMemo(() => {
    // must be able to do EITHER cc OR gift card payments
    if (canDoCCPayment) return true;
    if (canDoGiftCardPayment) return true;
    return false;
  }, [canDoCCPayment, canDoGiftCardPayment]);

  const canDoCashPayment = useMemo(() => {
    // must be allowed AND have valid payment methods
    if (!grantedPermissions.cashPayment.isAllowed) return false;
    if (!availableCashPaymentMethods.length) return false;
    return true;
  }, [availableCashPaymentMethods, grantedPermissions.cashPayment.isAllowed]);

  const canDoOtherPayment = useMemo(() => {
    // must be allowed AND have valid payment methods
    if (!grantedPermissions.otherPayment.isAllowed) return false;
    if (!grantedPermissions.otherPayment.paymentMethodIds.length) return false;
    return true;
  }, [grantedPermissions.otherPayment.isAllowed, grantedPermissions.otherPayment.paymentMethodIds.length]);

  const canDoManualPayment = useMemo(() => {
    // must be allowed AND have valid payment methods
    if (!grantedPermissions.manualPayment.isAllowed) return false;
    if (!grantedPermissions.manualPayment.paymentMethodIds.length) return false;
    return true;
  }, [grantedPermissions.manualPayment.isAllowed, grantedPermissions.manualPayment.paymentMethodIds.length]);

  const canDoSplitOrPartialPayment = useMemo(() => {
    // must have non-zero check balance AND be able to do ANY of the payment types
    if (currentCheck.query.data?.balance.unpaid === 0) return false;
    if (canDoCashPayment) return true;
    if (canDoCCPayment) return true;
    if (canDoGiftCardPayment) return true;
    if (canDoManualPayment) return true;
    if (canDoOtherPayment) return true;
    return false;
  }, [canDoCCPayment, canDoCashPayment, canDoGiftCardPayment, canDoManualPayment, canDoOtherPayment, currentCheck.query.data?.balance.unpaid]);

  const canDoSplitCheck = useMemo(() => grantedPermissions.splitCheck.isAllowed, [grantedPermissions.splitCheck.isAllowed]);

  // handle flip and tip re-init when we first route back from
  //  - dependent on current payment flow intent
  //  - dependent on current check unpaid amount
  //  - "single fire" operation when unpaid amount changes
  useEffect(() => {
    // helper for first mount checking
    if (typeof checkId !== 'number') return;

    // check for "afterFlipAndTip" case first
    if (paymentFlow.is.afterFlipAndTip) {
      // !!! only fire once when coming back from flip and tip
      paymentFlow.is.afterFlipAndTip = false;
      if (paymentFlow.has.split) return modal.execute.open();
    }
  }, [checkId, modal.execute, paymentFlow.has.split, paymentFlow.is]);

  return {
    ref,
    currentPayment,
    modal,
    otherPayment,
    currentCheck,
    reopenCheck,
    printPaymentReceipt,
    printGuestReceipt,
    creditCardPayment,
    can: {
      doCardReaderPayment: canDoCardReaderPayment,
      doCCPayment: canDoCCPayment,
      doGiftCardPayment: canDoGiftCardPayment,
      doCashPayment: canDoCashPayment,
      doSplitOrPartialPayment: canDoSplitOrPartialPayment,
      doSplitCheck: canDoSplitCheck,
      doOtherPayment: canDoOtherPayment,
      doManualPayment: canDoManualPayment,
    },
    manual: {
      onPay: manualCreditCardPayment,
    },
    cash: {
      isPending: createCash.isPending,
      paymentMethods: availableCashPaymentMethods,
      onPay: (amount: number) => {
        void cashPayment(amount);
      },
      buttons: useMemo(() => {
        const buttons = new Map<number, number>();
        const baseBalance = check?.balance.cashDiscountedTotal ?? check?.balance.unpaid ?? 0;
        const btnFirst = Math.ceil(baseBalance);
        let btnSecond = 0;
        let btnThird = 0;
        if (baseBalance < 50) {
          btnSecond = Math.ceil(baseBalance / 10) * 10;
          btnThird = btnSecond + 10;
        } else if (baseBalance >= 50) {
          btnSecond = Math.ceil(baseBalance / 20) * 20;
          btnThird = btnSecond + 20;
        }
        buttons.set(1, btnFirst);
        buttons.set(2, btnSecond);
        buttons.set(3, btnThird);
        return buttons;
      }, [check?.balance.cashDiscountedTotal, check?.balance.unpaid]),
    },
    onSplitCheckClick: async () => {
      if (typeof checkId !== 'number') return log.debug('onSplitCheckClick', '!checkId');

      navigate(crumbs.pos.check.split.main.interpolate({
        checkId,
        itemSelect: route.params.active.itemSelect,
      }));
    },
    paymentFlow,
  };
};

export { useCheckPagePayment };

